import {put, call, all} from 'redux-saga/effects';
import EntityTimestamp from "../../entities/entityTimestamp";
import * as Actions from "../actions/entityTimestampActions";
import * as idb from '../../lib/idbd';
import * as api from '../../rest/api';
import {processQueue} from "../../queues/processQueue";
import newHRTimestamp from "../../lib/picos/timestamp";
import {ensureOperationId} from '../../lib/pendingRows';
import {tabSession} from '../../Auth/session';

// Inlined to reduce circular dependencies
function getCurrentUserId() {
    return sessionStorage.getItem('userId');
}

// Inlined to reduce circular dependencies
function getCurrentFirmId() {
    return sessionStorage.getItem('firmId');
}

type EntityTimestampAction = {
    entityTimestampId: string;
    entityTimestamp: EntityTimestamp;
    entityTimestamps: EntityTimestamp[];
    sentAddedOn?: number;
    onQueued?: (saved: boolean) => void;
};

export function* fetchOneEntityTimestamp(action: EntityTimestampAction) {
	const session = tabSession.captureGeneration();
	try {
		const entityTimestamp: EntityTimestamp = yield call(api.findOne, 'entityTimestamp', action.entityTimestampId);
		session.assertCurrent();

		// Now we can update the user interface
		yield put({type: Actions.ENTITYTIMESTAMP_FETCH_ONE_SUCCESS, entityTimestampId: action.entityTimestampId, entityTimestamp, sentAddedOn: action.sentAddedOn});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.ENTITYTIMESTAMP_FETCH_ONE_FAILURE, entityTimestampId: action.entityTimestampId, status: 500, err});

		console.error('Error fetching from database', err);
	}
}

/**
 * @param {EntityTimestampAction} action
 */
export function* saveEntityTimestamp(action: EntityTimestampAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    try {
        // console.debug('⮂ ENTITYTIMESTAMP_SAVE saga', action.entityTimestamp?.id);

		if (action.entityTimestamp === undefined || action.entityTimestamp === null) {
			console.error('saveEntityTimestamp received a null');
			throw new Error('saveEntityTimestamp received a null');
		}

		if (!(action.entityTimestamp instanceof EntityTimestamp)) {
			action.entityTimestamp = new EntityTimestamp(action.entityTimestamp);
		}
		// OWA-1018: the reducer showed this save at dispatch, stamped after the version this tab holds
		// (OWA-1083), marked pending under this queue key
		const dispatchedAddedOn = action.entityTimestamp.addedOn;

		try {
			// Save to IndexedDB
			ensureOperationId(action.entityTimestamp);
			const addResult = yield call(idb.add, 'entityTimestamp', action.entityTimestamp);
			if (addResult.isErr()) {
				console.error(`⮂ saveEntityTimestamp: IDB write failed: ${addResult.unwrapErr().message}`);
				reportQueued(false);
				session.assertCurrent();
				// The save never reached the queue: its row returns to the latest accepted version
				yield put(Actions.revertEntityTimestampSave(action.entityTimestamp, {sentAddedOn: dispatchedAddedOn}));
				yield put({type: Actions.ENTITYTIMESTAMP_SAVE_FAILURE, entityTimestamp: action.entityTimestamp, status: 500, message: addResult.unwrapErr().message});
				return;
			}
			session.assertCurrent();
			reportQueued(true);

			// Now we can update the user interface: the queued copy replaces the row shown at dispatch
			// (the queue may have stamped it again)
			yield put({type: Actions.ENTITYTIMESTAMP_SAVE, entityTimestamp: action.entityTimestamp, dispatchedAddedOn, queuedAt: performance.now()});

			// A save never deletes autosaved drafts (OWA-958). The editor that wrote a draft
			// removes it when it saves; a draft typed somewhere else may be newer than this copy.
			session.assertCurrent();

			// Let the queue know to process
			yield call(processQueue);
		}
		catch(err) {
			if (session.signal.aborted) { reportQueued(false); return; }
			// OWA-1018: a save that never reached the queue returns its row to the latest accepted version
			if (!queueReported) yield put(Actions.revertEntityTimestampSave(action.entityTimestamp, {sentAddedOn: dispatchedAddedOn}));
			reportQueued(false);
			yield put({type: Actions.ENTITYTIMESTAMP_SAVE_FAILURE, entityTimestamp: action.entityTimestamp, status: 500, err});

			console.error('Error saving to database', err);
		}
    } finally { reportQueued(false); }
}

/**
 * TODO: Update for saveMany
 * @param {EntityTimestampAction} action
 * @param {EntityTimestamp[]} action.entityTimestamps
 */
export function* saveManyEntityTimestamps(action: EntityTimestampAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    try {
        // console.debug('⮂ ENTITYTIMESTAMP_SAVE_MANY saga', action.entityTimestamps.map(entityTimestamp => entityTimestamp.id).join(','));

        if (!Array.isArray(action.entityTimestamps)) {
            console.error('saveManyEntityTimestamps expected an array, received', action.entityTimestamps);
            throw new Error('saveManyEntityTimestamps expected an array');
        }
		// OWA-1018: the reducer showed these saves at dispatch (see saveEntityTimestamp), under these queue keys
		const dispatchedAddedOns: number[] = action.entityTimestamps.map(entityTimestamp => entityTimestamp?.addedOn);
		const revertAll = () => action.entityTimestamps.map((entityTimestamp, i) =>
			put(Actions.revertEntityTimestampSave(entityTimestamp, {sentAddedOn: dispatchedAddedOns[i]})));

		try {
			const toSave: EntityTimestamp[] = [];

			// Save each item to IndexedDB
			for(let i = 0; i < action.entityTimestamps.length; i++) {
				const entityTimestamp = action.entityTimestamps[i];
				entityTimestamp.persisted = false;
				ensureOperationId(entityTimestamp);
				toSave.push(entityTimestamp);
			}

			const addResult = yield call(idb.add, 'entityTimestamp', toSave);
			if (addResult.isErr()) {
				console.error(`⮂ saveManyEntityTimestamps: IDB write failed: ${addResult.unwrapErr().message}`);
				reportQueued(false);
				session.assertCurrent();
				// The saves never reached the queue: their rows return to the latest accepted versions
				yield all(revertAll());
				yield put({type: Actions.ENTITYTIMESTAMP_SAVE_MANY_FAILURE, entityTimestamps: action.entityTimestamps, status: 500, message: addResult.unwrapErr().message});
				return;
			}
			session.assertCurrent();
			reportQueued(true);

			// Now we can update the user interface: the queued copies replace the rows shown at dispatch
			yield put({type: Actions.ENTITYTIMESTAMP_SAVE_MANY, entityTimestamps: toSave, dispatchedAddedOns, queuedAt: performance.now()});
			session.assertCurrent();

			// Let the queue know to process
			yield call(processQueue)
		}
		catch(err) {
			if (session.signal.aborted) { reportQueued(false); return; }
			// OWA-1018: saves that never reached the queue return their rows to the latest accepted versions
			if (!queueReported) yield all(revertAll());
			reportQueued(false);
			yield put({type: Actions.ENTITYTIMESTAMP_SAVE_MANY_FAILURE, entityTimestamps: action.entityTimestamps, status: 500, err});

			console.error(`Error saving ${action.entityTimestamps.length} entityTimestamps to database`, err);
		}
    } finally { reportQueued(false); }
}

/**
 * @param {EntityTimestampAction} action
 */
export function* removeEntityTimestamp(action: EntityTimestampAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
	if (!userId || action.entityTimestamp === undefined || action.entityTimestamp === null || !(action.entityTimestamp instanceof EntityTimestamp)) {
			console.error('removeEntityTimestamp expected type EntityTimestamp, received', action.entityTimestamp);
			reportQueued(false);
			throw new Error('removeEntityTimestamp expected type EntityTimestamp');
		}

	try {
		// Mark as deleted. The stamp follows the version this tab holds (the reducer raised the clock, OWA-1083).
		action.entityTimestamp.status = 'D';
		action.entityTimestamp.addedOn = newHRTimestamp();
		action.entityTimestamp.addedBy = userId;

		// Save to IndexedDB
		ensureOperationId(action.entityTimestamp);
		const addResult = yield call(idb.add, 'entityTimestamp', action.entityTimestamp);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ removeEntityTimestamp: IDB write failed: ${addResult.unwrapErr().message}`);
			reportQueued(false);
			yield put({type: Actions.ENTITYTIMESTAMP_REMOVE_FAILURE, entityTimestamp: action.entityTimestamp, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.ENTITYTIMESTAMP_REMOVE, entityTimestamp: action.entityTimestamp});
		session.assertCurrent();

        reportQueued(true);

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.ENTITYTIMESTAMP_REMOVE_FAILURE, entityTimestamp: action.entityTimestamp, status: 500, err});

		console.error('Error deleting from database', err);
	}
    finally { reportQueued(false); }
}

/**
	* Restore the last version of this EntityTimestamp.
	*
	* @param {EntityTimestampAction} action
	*/
export function* restoreEntityTimestamp(action: EntityTimestampAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
    if (!userId) {
        console.error('restoreEntityTimestamp expected getCurrentUserId() to return a value');
        return;
    }

	if (action.entityTimestamp === undefined || action.entityTimestamp === null) {
        console.error('restoreEntityTimestamp expected a EntityTimestamp, received null');
        throw new Error('restoreEntityTimestamp expected a EntityTimestamp, received null');
    }
    else if (!(action.entityTimestamp instanceof EntityTimestamp)) action.entityTimestamp = new EntityTimestamp(action.entityTimestamp); 

	try {
		// Mark as restored. The stamp follows the version this tab holds (the reducer raised the clock, OWA-1083).
		action.entityTimestamp.status = 'U';
		action.entityTimestamp.addedOn = newHRTimestamp();
		action.entityTimestamp.addedBy = userId;
		action.entityTimestamp.restore = true;

		// Save to IndexedDB
		ensureOperationId(action.entityTimestamp);
		const addResult = yield call(idb.add, 'entityTimestamp', action.entityTimestamp);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ restoreEntityTimestamp: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.ENTITYTIMESTAMP_RESTORE_FAILURE, entityTimestamp: action.entityTimestamp, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.ENTITYTIMESTAMP_RESTORE, entityTimestamp: action.entityTimestamp});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.ENTITYTIMESTAMP_RESTORE_FAILURE, entityTimestamp: action.entityTimestamp, status: 500, err});

		console.error('Error restoring from database', err);
	}
}